import React from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';

import { useTranslation } from 'react-i18next';

import { RegType, Visits } from '../../../../../../core/types/health.types';
import { languagePreferenceSelector } from '../../../../../../store/participant/selectors';
import { useAppSelector } from '../../../../../../store';

interface VisitsProps {
  visits: Visits[];
  page?: number;
  rowsPerPage?: number;
}

const visitColours = {
  in: '#5ABBE5',
  out: '#92C15F',
  er: '#EA516D',
  default: '#F2AE21',
};

function VisitsList({ visits, page = 0, rowsPerPage = 10 }: VisitsProps) {
  const { t } = useTranslation('portal');
  const languagePreference = useAppSelector(languagePreferenceSelector);

  const format = languagePreference === 'en' ? 'DD MMM YYYY' : 'DD/MM/YYYY';

  const formatDate = (start: string, end: string) =>
    start === end ? moment(start).format(format) : `${moment(start).format(format)} - ${moment(end).format(format)}`;

  const getVisitType = (registrationType: RegType) => {
    switch (registrationType) {
      case RegType.IN:
        return {
          label: t('health.visits.in'),
          colour: visitColours.in,
          Icon: LoginOutlined,
        };

      case RegType.OUT:
        return {
          label: t('health.visits.out'),
          colour: visitColours.out,
          Icon: LogoutOutlined,
        };

      case RegType.ER:
        return {
          label: t('health.visits.er'),
          colour: visitColours.er,
          Icon: LocalHospitalOutlined,
        };

      default:
        return {
          label: '',
          colour: visitColours.default,
          Icon: LocalHospitalOutlined,
        };
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {visits
        .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
        .map(({ startDate, endDate, visitLocation, department, registrationType }: Visits, index: number) => {
          const { label, colour, Icon } = getVisitType(registrationType);

          return (
            <Paper
              key={`${startDate}-${endDate}-${department}-${visitLocation}-${index}`}
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                transition: 'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
                '&:hover': {
                  borderColor: colour,
                  boxShadow: `0 12px 30px ${colour}22`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, md: 2.5 },
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${colour}18`,
                    color: colour,
                    border: 1,
                    borderColor: `${colour}44`,
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 30 }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 0.75,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarMonthOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />

                      <Typography variant="body2" component="p" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {formatDate(startDate, endDate)}
                      </Typography>
                    </Box>

                    {label && (
                      <Chip
                        label={label}
                        size="small"
                        sx={{
                          height: 24,
                          fontWeight: 800,
                          bgcolor: `${colour}18`,
                          color: colour,
                          border: 1,
                          borderColor: `${colour}44`,
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    Inkosi Albert Luthulu Central Hospital
                  </Typography>

                  <Typography
                    variant="body2"
                    component="p"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      fontWeight: 700,
                    }}
                  >
                    {department} - {visitLocation}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
    </Box>
  );
}

export default VisitsList;
